import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CLOSED_MESSAGE = 'Jelenleg zárva vagyunk, rendelést most nem tudunk fogadni.';

/**
 * Restaurant status hook.
 * Status is ALWAYS controlled manually from the admin panel.
 * No automatic opening/closing logic.
 *
 * Fail-closed semantics:
 * - Initial state is fully closed (all flags false).
 * - On non-ok response, network error, or invalid payload, all flags
 *   are set to false so the UI never optimistically appears "open".
 *
 * Loading semantics:
 * - `loaded` starts false and becomes true once the first response (or error)
 *   is processed. Consumers should gate any "closed" banner on `loaded` so
 *   the page does not flash a false ZARVA message during the initial fetch
 *   (especially during the Render free-tier cold start, which can last
 *   several minutes).
 */
export const useRestaurantStatus = () => {
  const [loaded, setLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [takeawayEnabled, setTakeawayEnabled] = useState(false);
  const [dineinEnabled, setDineinEnabled] = useState(false);
  const [statusMessage, setStatusMessage] = useState(CLOSED_MESSAGE);
  // Vacation ("szabadság") banner state — drives the public site-wide pop-up.
  const [vacationEnabled, setVacationEnabled] = useState(false);
  const [vacationStart, setVacationStart] = useState('');
  const [vacationEnd, setVacationEnd] = useState('');

  const failClosed = () => {
    setIsOpen(false);
    setDeliveryEnabled(false);
    setTakeawayEnabled(false);
    setDineinEnabled(false);
    setStatusMessage(CLOSED_MESSAGE);
    // Fail closed for the vacation pop-up too: on any error we do NOT show it,
    // so a network hiccup never displays a false "we are on holiday" message.
    setVacationEnabled(false);
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/restaurant/status`);
      if (!response.ok) {
        failClosed();
        return;
      }
      const data = await response.json();

      setIsOpen(data.isOpen ?? false);
      setDeliveryEnabled(data.deliveryEnabled ?? false);
      setTakeawayEnabled(data.takeawayEnabled ?? false);
      setDineinEnabled(data.dineinEnabled ?? false);
      setVacationEnabled(data.vacationEnabled ?? false);
      setVacationStart(data.vacationStart ?? '');
      setVacationEnd(data.vacationEnd ?? '');

      if (!data.isOpen) {
        setStatusMessage(CLOSED_MESSAGE);
      } else {
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Error fetching restaurant status:', error);
      failClosed();
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000); // Re-check every 60s
    return () => clearInterval(interval);
  }, []);

  return {
    loaded,
    isOpen,
    deliveryEnabled,
    takeawayEnabled,
    dineinEnabled,
    statusMessage,
    vacationEnabled,
    vacationStart,
    vacationEnd,
  };
};
